import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import { banco } from '../../db/banco'
import { novoRegistroBase } from '../../db/tipos'
import { gravarLocalEEnfileirar } from '../../sync/outbox'
import { ROTULOS_TIPO_META } from '../../metas/rotulos'
import { montarSugestao, type MontagemCriador } from '../../metas/criador'
import { primeiroDiaDoMes, ultimoDiaDoMes, type Base } from '../../metas/destrave'
import { PADRAO_METAS } from '../../metas/configuracao'
import { lerAlvos, camposIniciais } from '../../metas/alvos'
import { useSalvamento } from '../../componentes/useSalvamento'
import BotaoSalvar from '../../componentes/BotaoSalvar'

const CAMPO = 'w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm'

const ROTULO_BASE: Record<Base, string> = {
  tres_meses: '3 meses',
  seis_meses: '6 meses',
  anual: 'anual',
}

function mesSeguinte(hoje: Date): string {
  const d = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export default function CriadorDeMetas() {
  const navegar = useNavigate()
  const [escopo, setEscopo] = useState('pessoal')
  const [base, setBase] = useState<Base>('tres_meses')
  const [pct, setPct] = useState(PADRAO_METAS.pctDinheiro)
  const [montagem, setMontagem] = useState<MontagemCriador | null>(null)
  const [alvos, setAlvos] = useState<Record<string, string>>({})
  const [aviso, setAviso] = useState<string | null>(null)
  // Enquanto ela nao mexeu em nenhum valor, a tela segue o que o banco tem.
  // Depois que mexeu, para de seguir -- senao uma sincronizacao chegando no
  // meio apagaria o numero que ela esta digitando. Mesma flag da tela de
  // configuracao das metas.
  const [tocado, setTocado] = useState(false)
  const { estado, executar, reiniciar } = useSalvamento()

  const mesAlvo = mesSeguinte(new Date())
  const representadas = useLiveQuery(
    () => banco.representadas.filter((r) => r.apagado_em === null).sortBy('nome'), [])

  // ⚠ montarSugestao le o banco LOCAL. Num aparelho que ainda nao sincronizou
  // (o celular dela, cache limpo), as vendas do servidor chegam DEPOIS que esta
  // tela montou -- e sem isto o criador diria "faltam X dias para destravar"
  // com tres anos de venda gravados. Mesmo defeito que a tela de configuracao
  // teve, achado pelo E2E em 2026-08-05.
  //
  // As TRES contagens, nao so a de pedidos: a sincronizacao pode trazer os
  // pedidos antes dos sub-pedidos, e a conta feita nesse meio do caminho sai
  // menor sem nada avisar.
  const versaoDosDados = useLiveQuery(async () => {
    const [pedidos, subs, itens] = await Promise.all([
      banco.pedidos.count(), banco.sub_pedidos.count(), banco.itens_pedido.count(),
    ])
    return `${pedidos}-${subs}-${itens}`
  }, [])

  // Mexer no escopo, na base ou no percentual refaz a sugestao inteira, e os
  // campos voltam para o valor sugerido novo: manter o que ela digitou em cima
  // de outra base seria misturar duas contas diferentes na mesma tela.
  useEffect(() => {
    if (tocado) return
    let atual = true
    setMontagem(null)
    void montarSugestao(escopo, base, mesAlvo, new Date(), pct).then((m) => {
      if (!atual) return
      setMontagem(m)
      setAlvos(camposIniciais(m.resultado.sugestoes))
      setAviso(null)
    })
    // A leitura do banco e assincrona: sem esta bandeira, trocar de base duas
    // vezes rapido deixa a resposta da PRIMEIRA chegar por ultimo e sobrescrever
    // a segunda -- a tela mostraria numeros de uma base que ela ja abandonou.
    return () => {
      atual = false
    }
  }, [escopo, base, mesAlvo, pct, versaoDosDados, tocado])

  function mudarAlvo(tipo: string, texto: string) {
    reiniciar()
    setAviso(null)
    setTocado(true)
    setAlvos((atuais) => ({ ...atuais, [tipo]: texto }))
  }

  // Trocar escopo, base ou percentual e pedir uma conta NOVA: aqui sobrescrever
  // os campos e o que ela espera, ao contrario da sincronizacao chegando sozinha.
  function refazer(alteracao: () => void) {
    reiniciar()
    setAviso(null)
    setTocado(false)
    alteracao()
  }

  async function aceitar() {
    if (!montagem) return
    const { metas, invalidos } = lerAlvos(
      montagem.resultado.sugestoes.map((s) => s.tipo),
      alvos,
    )
    if (invalidos.length > 0) {
      setAviso(
        `Confira o valor de: ${invalidos.map((t) => ROTULOS_TIPO_META[t]).join(', ')}.`,
      )
      return
    }
    if (metas.length === 0) {
      setAviso('Preencha ao menos uma meta para criar.')
      return
    }
    setAviso(null)
    await executar(async () => {
      for (const m of metas) {
        // Sem tenant_id e sem `as`: RegistroBase.tenant_id e opcional e o
        // servidor preenche. Igual a Metas.tsx:48-51.
        await gravarLocalEEnfileirar('metas', {
          ...novoRegistroBase(),
          escopo,
          tipo: m.tipo,
          periodo_inicio: primeiroDiaDoMes(mesAlvo),
          periodo_fim: ultimoDiaDoMes(mesAlvo),
          valor_alvo: m.valor,
        })
      }
      // Dentro do executar de proposito: se a gravacao falhar, a excecao sobe,
      // o botao mostra o erro e a tela NAO navega -- sair daqui com falha
      // esconderia o problema.
      navegar('/metas')
    })
  }

  const destrave = montagem?.destrave

  return (
    <section className="p-4 space-y-4">
      <h2 className="text-lg font-bold text-slate-800">Criador de metas — {mesAlvo}</h2>

      <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
        <label className="block">
          <span className="block text-xs text-slate-500 mb-0.5">De</span>
          <select
            data-testid="escopo" value={escopo}
            onChange={(e) => refazer(() => setEscopo(e.target.value))} className={CAMPO}
          >
            <option value="pessoal">Pessoal (minhas metas)</option>
            {representadas?.map((r) => (
              <option key={r.id} value={`representada:${r.id}`}>{r.nome}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs text-slate-500 mb-0.5">Base</span>
          <select
            data-testid="base" value={base}
            onChange={(e) => refazer(() => setBase(e.target.value as Base))} className={CAMPO}
          >
            {(Object.keys(ROTULO_BASE) as Base[]).map((b) => (
              <option key={b} value={b}>{ROTULO_BASE[b]}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs text-slate-500 mb-0.5">Crescer (%)</span>
          <input
            data-testid="pct" type="number" className={CAMPO} value={pct}
            onChange={(e) => refazer(() => setPct(Number(e.target.value)))}
          />
        </label>
      </div>

      {destrave && !destrave.destravado && (
        <div
          data-testid="criador-travado"
          className="bg-amber-100 border border-amber-300 rounded-xl p-4 text-sm text-amber-900"
        >
          <p>
            Para o criador de metas sugerir automaticamente suas metas é preciso no mínimo
            3 meses de informações completas das suas vendas.
          </p>
          {destrave.dataDeDestrave ? (
            <p className="mt-2 font-bold">
              Faltam {destrave.diasFaltando} dias para destravar essa função.
            </p>
          ) : (
            <p className="mt-2 font-bold">Ainda não há vendas registradas.</p>
          )}
        </div>
      )}

      {montagem && destrave?.destravado && !montagem.resultado.podeSugerir && (
        <div
          data-testid="criador-sem-base"
          className="bg-amber-100 border border-amber-300 rounded-xl p-4 text-sm text-amber-900"
        >
          {montagem.resultado.motivo}
        </div>
      )}

      {montagem?.resultado.podeSugerir && (
        <>
          <div className="bg-white rounded-xl shadow-sm divide-y">
            {montagem.resultado.sugestoes.map((s) => (
              <div key={s.tipo} className="p-3" data-testid={`sugestao-${s.tipo}`}>
                <span className="block text-sm font-medium text-slate-800">
                  {ROTULOS_TIPO_META[s.tipo]}
                </span>
                <span className="block text-xs text-slate-500 mb-1">
                  média {s.media} em {s.mesesUsados} {s.mesesUsados === 1 ? 'mês' : 'meses'}
                </span>
                <input
                  data-testid={`alvo-${s.tipo}`} className={CAMPO} value={alvos[s.tipo] ?? ''}
                  onChange={(e) => mudarAlvo(s.tipo, e.target.value)}
                />
              </div>
            ))}
          </div>

          {aviso && (
            <p data-testid="aviso-alvos" className="text-sm text-amber-700">{aviso}</p>
          )}

          <div data-testid="aceitar-sugestoes">
            <BotaoSalvar estado={estado} type="button" onClick={() => void aceitar()}>
              Aceitar as metas
            </BotaoSalvar>
          </div>
        </>
      )}

      <button
        onClick={() => navegar('/metas')}
        className="w-full border border-teal-700 text-teal-700 rounded-lg py-2 font-medium"
      >
        Voltar
      </button>
    </section>
  )
}
